import React, { useEffect, useState, Suspense } from "react";
import { Button } from "../../../shared/components/ui/button";
import { googleIcon } from "../../../shared/constants/images";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useFormContext } from "react-hook-form";

function GoogleAuthButtonContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isBasicInfoValid, setIsBasicInfoValid] = useState(true); // Default to true for login context
  const [isSignupContext, setIsSignupContext] = useState(false);
  
  // Get form context - it might be SignupFormType or LoginFormType
  // Both LoginFormProvider and SignupFormProvider provide a form context
  // We'll check the structure to determine which one
  const formContext = useFormContext<any>();

  // Check if we're in signup context and validate fields accordingly
  useEffect(() => {
    try {
      const values = formContext?.getValues?.();
      // Check if this is a signup context by checking for form1 structure
      if (values && 'form1' in values) {
        setIsSignupContext(true);
        setIsBasicInfoValid(false); // Start as false for signup, will be validated below
        
        // Watch the required fields (fullname, phone) from form1
        // Email is not required for Google sign in button activation
        const checkBasicInfo = () => {
          const currentValues = formContext.getValues();
          const fullname = currentValues?.form1?.fullname?.trim();
          const phone = currentValues?.form1?.phone?.trim();
          
          const isValid = !!(fullname && phone);
          setIsBasicInfoValid(isValid);
        };

        // Initial check
        checkBasicInfo();

        // Watch for changes
        const subscription = formContext.watch((value: any) => {
          checkBasicInfo();
        });
        
        return () => subscription.unsubscribe();
      } else {
        // Login context - button should always be enabled
        setIsSignupContext(false);
        setIsBasicInfoValid(true);
      }
    } catch (error) {
      // Error getting values - assume login context
      setIsSignupContext(false);
      setIsBasicInfoValid(true);
    }
  }, [formContext]);

  const handleGoogleAuth = async () => {
    // Only validate and store basic info if we're in signup context
    if (formContext && isSignupContext) {
      const values = formContext.getValues();
      const fullname = values?.form1?.fullname?.trim();
      const email = values?.form1?.email?.trim(); // Email is optional for Google sign in
      const phone = values?.form1?.phone?.trim();
      
      // Email is not required for Google sign in, only fullname and phone are required
      if (!fullname || !phone) {
        return; // Don't proceed if required fields are missing
      }
      
      // Store form data in sessionStorage AND localStorage before redirecting
      // Use both to ensure data persists through OAuth redirects
      // Also store a flag to indicate this is from signup page
      // Email is optional, so only include it if provided
      const signupData: {
        fullname: string;
        phone: string;
        fromSignupPage: boolean;
        email?: string;
      } = {
        fullname,
        phone,
        fromSignupPage: true, // Flag to indicate this is from signup
      };
      
      // Only include email if it was provided
      if (email) {
        signupData.email = email;
      }
      
      console.log('[GoogleAuth] Storing signup info in sessionStorage and localStorage:', signupData);
      sessionStorage.setItem('googleSignupInfo', JSON.stringify(signupData));
      localStorage.setItem('googleSignupInfo', JSON.stringify(signupData)); // Backup in localStorage
      console.log('[GoogleAuth] Signup info stored, redirecting to Google OAuth...');
    } else {
      // Login context - store flag to indicate this is from login page
      // This will be used in the callback to check if email exists
      console.log('[GoogleAuth] Login context - storing fromLoginPage flag');
      sessionStorage.setItem('googleLoginFromLoginPage', 'true');
      localStorage.setItem('googleLoginFromLoginPage', 'true');
      // Clear any existing signup info to prevent false positives
      sessionStorage.removeItem('googleSignupInfo');
      localStorage.removeItem('googleSignupInfo');
    }
    
    const baseUrl = import.meta.env.VITE_BASE_BACKEND_URL || '/api';
    // Pass state parameter to indicate login vs signup
    // Google OAuth will return this state parameter in the callback
    const state = isSignupContext ? 'signup' : 'login';
    
    // Build state params: role only from current URL (for QR flyers), region/team from sessionStorage.
    // Role must come from URL to avoid stale sessionStorage (e.g. after account deletion + recreating).
    const urlRole = searchParams.get('role');
    const unifiedAuthRegion = sessionStorage.getItem('unifiedAuthRegion');
    const unifiedAuthRole = urlRole || null; // Only trust role from current page URL
    const unifiedAuthTeam = sessionStorage.getItem('unifiedAuthTeam');
    
    console.log('[GoogleAuth] Building state - role from URL only:', {
      urlRole,
      unifiedAuthRegion,
      unifiedAuthRole,
      unifiedAuthTeam,
    });
    
    let stateWithParams = state;
    if (unifiedAuthRegion || unifiedAuthRole || unifiedAuthTeam) {
      const params: string[] = [];
      if (unifiedAuthRegion) params.push(`region=${unifiedAuthRegion}`);
      if (unifiedAuthRole && (unifiedAuthRole === 'volunteer' || unifiedAuthRole === 'homeowner')) {
        params.push(`role=${unifiedAuthRole}`);
      }
      if (unifiedAuthTeam) params.push(`team=${encodeURIComponent(unifiedAuthTeam)}`);
      if (params.length > 0) {
        stateWithParams = `${state}|${params.join('&')}`;
      }
      console.log('[GoogleAuth] Built state with params:', stateWithParams);
    }
    
    // Check if we have a previously selected Google account email (from a failed login attempt)
    // This allows us to use login_hint to pre-select the account and skip the selection page
    let selectedEmail = sessionStorage.getItem('selectedGoogleAccountEmail');
    if (!selectedEmail) {
      selectedEmail = localStorage.getItem('selectedGoogleAccountEmail');
    }
    
    // Build the OAuth URL with state and optional login_hint
    let oauthUrl = `${baseUrl}/auth/google?state=${encodeURIComponent(stateWithParams)}`;
    if (selectedEmail) {
      oauthUrl += `&login_hint=${encodeURIComponent(selectedEmail)}`;
      console.log('[GoogleAuth] Using login_hint to pre-select Google account:', selectedEmail);
    }
    
    window.location.href = oauthUrl;
  };

    return (
        <Button 
            type='button'
            variant={"outline"} 
            className='rounded-full w-full text-foreground border-input'
            onClick={handleGoogleAuth}
            disabled={isSignupContext && !isBasicInfoValid}
            showLoader={false}
        >
            <Image
                src={googleIcon}
                alt='Google svg icon'
                className='w-5 h-5'
            />
            Continue with Google
        </Button>
    )
}

function GoogleAuthButton() {
  return (
    <Suspense fallback={
      <Button 
        type='button'
        variant={"outline"} 
        className='rounded-full w-full text-foreground border-input'
        disabled
      >
        <Image
          src={googleIcon}
          alt='Google svg icon'
          className='w-5 h-5'
        />
        Loading...
      </Button>
    }>
      <GoogleAuthButtonContent />
    </Suspense>
  )
}

export default GoogleAuthButton;
